<template>
  <Collapsible
    v-if="filter?.buckets?.length"
    class="filter-checkbox__section"
    :isCollapsed="isCollapsed"
    :isCollapsible="true"
    :max-height="28"
    :min-height-to-collapse="filter.alwaysExpanded ? 10000 : 65"
    @toggle="onToggle"
  >
    <template #header>
      <div class="filter-checkbox__section-header">
        <input
          @click="onParentTagClick(filter.attribute, $event)"
          v-if="parentFilter"
          type="checkbox"
          :checked="parentSelected?.includes(filter.attribute)"
          class="mr-xs"
        />
        <h3 :style="parentFilter ? 'font-weight:400' : ''">
          {{ filter.title }}
        </h3>
        <UpDownArrow
          class="filter-checkbox__section-icon"
          :type="isCollapsed ? 'down' : 'up'"
        />
        <div class="filter-checkbox__section-selected-values">
          {{ selectedFilter?.join(',') }}
        </div>
      </div>
    </template>
    <template #body>
      <div class="filter-checkbox__options">
        <div
          class="filter-checkbox__option"
          :class="{ 'ml-md': parentFilter }"
          v-for="(bucket, index) in filter.buckets"
          :key="bucket.value + index"
        >
          <input
            :id="bucket.value + index"
            type="checkbox"
            :checked="isSelected(bucket.value)"
            @click="onTagClick(bucket.value)"
          />
          <label :for="bucket.value + index">
            {{ bucket.value }} {{ bucket.count ? `(${bucket.count})` : '' }}
          </label>
        </div>
      </div>
    </template>
  </Collapsible>
</template>

<script lang="ts" setup>
import Collapsible from '@/ui-kits/Collapsible.vue'
import UpDownArrow from '@/ui-kits/UpDownArrow.vue'
import { FilterSection } from '@/types'
import { useRoute } from 'vue-router'
import { onMounted, onUnmounted, ref } from 'vue'
import { EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE } from '@/utils'
import EventBus from '@/services/eventbus'

interface FilterSectionProps {
  filter: FilterSection | null
  selectedFilter: string[]
  isCollapsible?: boolean
  parentFilter?: string
  parentSelected?: string[]
}

const props = withDefaults(defineProps<FilterSectionProps>(), {
  filter: null,
  selectedFilter: () => [],
  isCollapsible: true,
})

const emit = defineEmits<{
  (e: 'toggle-section', value: boolean): void
  (e: 'toggle-filter', value: string): void
  (e: 'toggle-parent-filter', value: string): void
}>()

const route = useRoute()

const isCollapsed = ref(true)
const expandCollapseQueryPrefix = 'show_filter'
const queryExpandCollapseFilter: any = ref([])

onMounted(() => {
  expandCollapseFilter()
  if (!props.parentFilter)
    EventBus.on(EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE, expandCollapseFilter)
})

onUnmounted(() => {
  if (!props.parentFilter)
    EventBus.off(
      EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE,
      expandCollapseFilter
    )
})

function expandCollapseFilter() {
  // expand collapse filter
  for (const key in route.query) {
    if (!key.startsWith(`${expandCollapseQueryPrefix}_`)) continue
    if (Object.prototype.hasOwnProperty.call(route.query, key)) {
      const value = route.query[key]
      let keyWithoutPrefix = key
        ?.trim()
        ?.toLowerCase()
        ?.split(`${expandCollapseQueryPrefix}_`)
        .pop()
      keyWithoutPrefix?.replaceAll('_', '')
      keyWithoutPrefix?.replaceAll('-', '')
      if (value) queryExpandCollapseFilter.value.push(keyWithoutPrefix)
    }
  }

  let attributeFilter = props.filter?.attribute?.trim()?.toLowerCase()
  attributeFilter?.replaceAll('_', '')
  attributeFilter?.replaceAll('-', '')
  const isExpandCollapseFilter =
    queryExpandCollapseFilter.value.includes(attributeFilter)
  if (isExpandCollapseFilter) onToggle(false)
}

function onToggle(value: boolean) {
  isCollapsed.value = value
}

function onTagClick(value: string) {
  emit('toggle-filter', value)
}

function onParentTagClick(value: string, event: Event) {
  emit('toggle-parent-filter', value)
  event.stopPropagation()
}

function isSelected(value: string) {
  return props.selectedFilter?.includes(value)
}
</script>

<style lang="scss">
.filter-checkbox {
  &__section {
    margin-top: 1em;

    &.collapsible--always-expanded .filter__section-icon {
      display: none;
    }
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: solid 1px var(--border-color);

    h3 {
      font-size: 1em;
      margin: 0;
      line-height: 26px;
      white-space: nowrap;
    }

    .sib-arrow {
      min-width: var(--sib-arrow-width);
      margin-left: 10px;
    }

    input[type='checkbox'] {
      min-width: 18px;
      min-height: 18px;
    }
  }

  &__section-selected-values {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 1em;
    font-size: 0.875rem;
    text-align: right;
  }

  &__options {
    gap: 0.8em;
    margin-top: 0.5em;
    height: 100%;
    overflow: auto;
    &::-webkit-scrollbar-track {
      background-color: var(--background-color);
    }
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #b4b4b4;
      border-radius: 1rem;
    }

    .sib-button--tertiary {
      width: auto;
      font-size: 0.875rem;
      padding: 0.25rem 0.875rem;
      height: auto;
      border: solid 1px var(--border-color);
    }
  }
  &__option {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    input,
    label {
      cursor: pointer;
      line-height: 1.8;
    }

    input[type='checkbox'] {
      min-width: 18px;
      min-height: 18px;
    }
  }
}
</style>
